/**
 * WhatsApp Tools Client Actions
 *
 * This file contains all client-side WhatsApp tool actions for the Thunk Pattern.
 * These actions communicate with the page context to interact with WhatsApp Web.
 *
 * Tool-Based Architecture (Thunk Pattern):
 * Every tool supports dual-mode execution:
 * - Extension Mode (isExtension = true): Direct communication via content script + page context with WhatsApp
 * - Server Mode (isExtension = false): HTTP request to server → Engine via MQTT → WhatsApp
 *
 * IMPORTANT: All command names MUST use constants from WA_TOOL_COMMANDS
 * defined in @anthropic/bt-common/interfaces/whatsapp-tools-commands.interface.ts
 *
 * Usage:
 * ```typescript
 * import { WhatsappToolsClientActions } from '@common/tools/whatsapp';
 *
 * // In extension context - call static methods directly
 * const result = await WhatsappToolsClientActions.getWaMeInfoTool();
 * ```
 */

import { parseLabelViewSnapshot, type LabelViewSnapshot } from "@common-reducers/labels-reducer/label-view-stats";
import { postMessageWithResponse, isWireError, type WireResponse } from "@common/services/post-message-to-whatsapp-functions";
import { assertWaPageBridge } from "@common/services/page-bridge-error";
import { WA_TOOL_COMMANDS } from "../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type {
    WaSendMessageThunkResult,
    WaGetNewsletterResult,
    WaCreateNewsletterResult,
    WaGetMessageByKeyResult,
    WaCreateGroupResult,
    WaUpdateGroupNameResult,
    WaGetGroupDataResult,
    WaUpdateGroupDescriptionResult,
    WaAddGroupParticipantResult,
    WaRemoveGroupParticipantResult,
    WaSetGroupAdminRightsResult,
    WaRemoveGroupAdminRightsResult,
    WaSetGroupPictureResult,
    WaLeaveGroupResult,
    WaGetChatParticipantsResult,
    WaGetCommonGroupsResult,
    WaAccountRestrictionResult,
    WaChatSendRiskResult,
    WaGetChatObjectByChatIdResult,
    WaOpenChatResult,
    WaQueryMessagesArgs,
    WaQueryMessagesResult,
    WaLoadMessagesFromPhoneResult,
    WaGetMessageAckResult,
    WaGetPollResultsResult,
    WaGetMessagesAckDataResult,
    WaReactToMessageResult,
    WaForwardMessageResult,
    WaStarMessageResult,
    WaDeleteMessageResult,
    WaPinMessageResult,
    WaUnpinMessageResult,
    GetWaMeInfoResult,
    IsWhatsAppConnectedResult,
    WaTriggerLogoutResult,
    WaReloadPageResult,
    WaValidatePhoneNumberOrChatIdResult,
    WaGetLinkPreviewResult,
    WaQueryContactsAndChatsResult,
    WaGetLatestChatsAndMessagesResult,
    WaQueryVaultMessagesArgs,
    WaQueryVaultMessagesResult,
    WaGetProfilePicUrlResult,
    GetUnloggedWaMessageKeyIfExistInStoreResult,
    WaToolResult,
} from "../../../bt-common/interfaces/whatsapp-tool-contracts.interface";
import type { UserMessage } from "../../../bt-common/schemas/bt/user-message";
import type {
    SendSeenResult,
    ClearMessagesResult,
    DeleteChatResult,
    ArchiveChatResult,
    UnarchiveChatResult,
    PinChatResult,
    UnpinChatResult,
    MuteChatResult,
    UnmuteChatResult,
    MarkChatUnreadResult,
    SendStateTypingResult,
    SendStateRecordingResult,
    ClearStateResult,
    GetChatLabelsResult,
    ChangeLabelsResult,
    GetPinnedMessagesResult,
    AddOrEditCustomerNoteResult,
} from "@common/tools/whatsapp/wa-tools-server-actions";

// ============================================================================
// WhatsApp Tools Client Actions Class
// ============================================================================

export class WhatsappToolsClientActions {

    // ==========================================
    // Contact Domain Tools
    // ==========================================



    public static async waGetProfilePicUrlTool(
        chatIds: string | string[],
    ): Promise<WireResponse<WaGetProfilePicUrlResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetProfilePicUrlTool, {
            chatIds: Array.isArray(chatIds) ? chatIds : [chatIds],
        });
    }


    // ==========================================
    // Chat Domain Tools
    // ==========================================

    /**
     * Get chat participants (for group chats)
     * Used by: waGetChatParticipantsTool
     */
    public static async waGetChatParticipantsTool(options: { chatId: string; limit?: number; offset?: number; searchToken?: string }): Promise<WireResponse<WaGetChatParticipantsResult> | undefined> {
        // Use a shorter timeout (3 seconds) for faster fallback
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetChatParticipantsTool, options, { timeoutOverride: 3000 });
    }

    /**
     * WhatsApp account-restriction (reachout timelock) + new-chat send quota.
     * Used by: waAccountRestrictionTool
     *
     * `refresh: true` asks WhatsApp before reading its cache — one MEX round-trip,
     * already bounded page-side. The page handler never throws; on failure it returns
     * `status: 'unavailable'`, which callers MUST NOT treat as "no restriction".
     */
    public static async waAccountRestrictionTool(
        options?: { refresh?: boolean },
    ): Promise<WireResponse<WaAccountRestrictionResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waAccountRestrictionTool, options ?? {});
    }

    /**
     * Per-recipient pre-send risk while the account is restricted.
     * Used by: waChatSendRiskTool
     *
     * Short timeout on purpose: this sits on the send path, and a slow answer must
     * never delay a send. A timeout surfaces as a wire error → the caller sends.
     */
    public static async waChatSendRiskTool(
        options: { chatId: string },
    ): Promise<WireResponse<WaChatSendRiskResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waChatSendRiskTool, options, { timeoutOverride: 3000 });
    }

    /**
     * Get the groups the current user shares with a contact ("Groups in common")
     * Used by: waGetCommonGroupsTool
     */
    public static async waGetCommonGroupsTool(options: { contactChatId: string }): Promise<WireResponse<WaGetCommonGroupsResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetCommonGroupsTool, options);
    }



    public static async getChatObjectByChatId(options: { chatId: string }): Promise<WireResponse<WaGetChatObjectByChatIdResult> | undefined> {
        // Use a shorter timeout (3 seconds) for faster fallback to server mode
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool, options, { timeoutOverride: 3000 });
    }
    /**
     * Send seen/read status for a chat
     * Used by: waSendSeenTool
     */
    public static async waSendSeenTool(chatId: string): Promise<WireResponse<SendSeenResult> | undefined> {
        // Use a shorter timeout (2 seconds) - this is not critical for UI.
        return postMessageWithResponse(WA_TOOL_COMMANDS.waSendSeenTool, { chatId }, { timeoutOverride: 2000 });
    }

    // Chat-state mutations have no WaToolContractMap entry — they fall through
    // to the legacy object-form. Result shape comes from the page handler emit
    // (see PageMessageService waXxxTool wireHandler), captured in
    // wa-tools-server-actions `*Result` types.
    public static async waClearMessagesTool(chatId: string): Promise<WireResponse<ClearMessagesResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waClearMessagesTool, { chatId });
    }

    public static async waGetNewsletterTool(params: { newsletterId: string }): Promise<WireResponse<WaGetNewsletterResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetNewsletterTool, params);
    }

    public static async waCreateNewsletterTool(params: { name: string; description?: string | null }): Promise<WireResponse<WaCreateNewsletterResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waCreateNewsletterTool, params, { timeoutOverride: 30000 });
    }

    public static async waDeleteChatTool(chatId: string): Promise<WireResponse<DeleteChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waDeleteChatTool, { chatId });
    }

    public static async waArchiveChatTool(chatId: string): Promise<WireResponse<ArchiveChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waArchiveChatTool, { chatId });
    }

    public static async waUnarchiveChatTool(chatId: string): Promise<WireResponse<UnarchiveChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waUnarchiveChatTool, { chatId });
    }

    public static async waPinChatTool(chatId: string): Promise<WireResponse<PinChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waPinChatTool, { chatId });
    }

    public static async waUnpinChatTool(chatId: string): Promise<WireResponse<UnpinChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waUnpinChatTool, { chatId });
    }

    public static async waMuteChatTool(chatId: string, unmuteDate?: string | number | null): Promise<WireResponse<MuteChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waMuteChatTool, { chatId, unmuteDate });
    }

    public static async waUnmuteChatTool(chatId: string): Promise<WireResponse<UnmuteChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waUnmuteChatTool, { chatId });
    }

    public static async waMarkChatUnreadTool(chatId: string): Promise<WireResponse<MarkChatUnreadResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waMarkChatUnreadTool, { chatId });
    }

    public static async waSendStateTypingTool(chatId: string): Promise<WireResponse<SendStateTypingResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waSendStateTypingTool, { chatId }, { timeoutOverride: 2000 });
    }

    public static async waSendStateRecordingTool(chatId: string): Promise<WireResponse<SendStateRecordingResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waSendStateRecordingTool, { chatId }, { timeoutOverride: 2000 });
    }

    public static async waClearStateTool(chatId: string): Promise<WireResponse<ClearStateResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waClearStateTool, { chatId }, { timeoutOverride: 2000 });
    }

    /** LID → c.us in the page context (a miss returns the input). Its only
     *  callers are the expired lid migrations (plan 7.14). */
    public static async convertWaLidSerializedToCusSerialized(waLidSerialized: string): Promise<string | undefined> {
        const response = await postMessageWithResponse(WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED, { waLidSerialized });
        return response && !isWireError(response) ? response.convertedId : undefined;
    }

    public static async waGetLabelsTool(): Promise<WireResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetLabelsTool>> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetLabelsTool, {});
    }

    public static async waGetChatLabelsTool(chatId: string): Promise<WireResponse<GetChatLabelsResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetChatLabelsTool, { chatId });
    }

    public static async waChangeLabelsTool(chatId: string, labelIds: Array<number | string>): Promise<WireResponse<ChangeLabelsResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waChangeLabelsTool, { chatId, labelIds });
    }

    public static async waGetPinnedMessagesTool(chatId: string): Promise<WireResponse<GetPinnedMessagesResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetPinnedMessagesTool, { chatId });
    }

    public static async waAddOrEditCustomerNoteTool(chatId: string, note: string): Promise<WireResponse<AddOrEditCustomerNoteResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool, { chatId, note });
    }

    /**
     * Open chat by chat ID or phone number.
     */
    public static async openChatById(chatId: string): Promise<WireResponse<WaOpenChatResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waOpenChatTool, { chatId });
    }

    // ==========================================
    // Group Domain Tools
    // ==========================================

    public static async waCreateGroupTool(params: {
        groupName: string;
        participantChatIds: string[];
    }): Promise<WireResponse<WaCreateGroupResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waCreateGroupTool, params);
    }

    public static async waUpdateGroupNameTool(params: {
        groupId: string;
        groupName: string;
    }): Promise<WireResponse<WaUpdateGroupNameResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waUpdateGroupNameTool, params);
    }

    public static async waGetGroupDataTool(params: {
        groupId: string;
    }): Promise<WireResponse<WaGetGroupDataResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetGroupDataTool, params);
    }

    public static async waUpdateGroupDescriptionTool(params: {
        groupId: string;
        settings: { announce?: boolean; restrict?: boolean; description?: string };
    }): Promise<WireResponse<WaUpdateGroupDescriptionResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool, params);
    }

    public static async waAddGroupParticipantTool(params: {
        groupId: string;
        participantChatId: string;
    }): Promise<WireResponse<WaAddGroupParticipantResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waAddGroupParticipantTool, params);
    }

    public static async waRemoveGroupParticipantTool(params: {
        groupId: string;
        participantChatId: string;
    }): Promise<WireResponse<WaRemoveGroupParticipantResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waRemoveGroupParticipantTool, params);
    }

    public static async waSetGroupAdminRightsTool(params: {
        groupId: string;
        participantChatId: string;
    }): Promise<WireResponse<WaSetGroupAdminRightsResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waSetGroupAdminRightsTool, params);
    }

    public static async waRemoveGroupAdminRightsTool(params: {
        groupId: string;
        participantChatId: string;
    }): Promise<WireResponse<WaRemoveGroupAdminRightsResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool, params);
    }

    public static async waSetGroupPictureTool(params: {
        groupId: string;
        fileDataUrl: string;
        fileName?: string;
        fileMimeType?: string;
    }): Promise<WireResponse<WaSetGroupPictureResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waSetGroupPictureTool, params, { timeoutOverride: 60 * 1000 });
    }

    public static async waLeaveGroupTool(params: {
        groupId: string;
    }): Promise<WireResponse<WaLeaveGroupResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waLeaveGroupTool, params);
    }
    
    /**
     * Update which chats should appear in the filtered list
     * Used by: waUpdateShouldAppearChatsByListTool
     */
    public static async waUpdateShouldAppearChatsByListTool(chatsThatShouldAppearInList: { [key: string]: boolean } | null | { filterType: string; excludeIds: string[] }): Promise<LabelViewSnapshot | undefined> {
        const response = await postMessageWithResponse(WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool, chatsThatShouldAppearInList);
        // #770: an allowlist filter answers with the label's visible/archived/no-conversation breakdown.
        return parseLabelViewSnapshot(response);
    }

    // ==========================================
    // Message Domain Tools
    // ==========================================

    /**
     * Unified Message Query
     *
     * Sends a single request to the page context which handles all message queries:
     * - Get messages from a chat (history/latest modes)
     * - Search messages by query
     * - Paginated message retrieval
     *
     * All logic is executed in the page context for direct access to WhatsApp stores.
     * Only non-default parameters should be passed to minimize payload size.
     *
     * @param params Query parameters (only non-defaults should be included)
     * @param params.chatId Chat ID for get mode (required unless searchQuery is provided)
     * @param params.searchQuery Search query for search mode
     * @param params.limit Maximum number of results (-1 for all)
     * @param params.offset Offset for pagination (only send if non-zero)
     * @param params.mode Fetch direction: 'history' | 'latest' (default: 'history')
     * @param params.cutoffDate ISO date string - get messages after this date
     * @param params.latestDate ISO date string - get messages before this date
     * @param params.slim Remove large bodies from media messages (default: true)
     * @param params.tail Get messages from end of chat
     * @param params.loadFromPhoneIfNeeded Try to load older messages from phone if not enough in store
     */
    public static async waQueryMessagesTool(
        params: WaQueryMessagesArgs,
    ): Promise<WireResponse<WaQueryMessagesResult> | undefined> {
        // Use longer timeout for large data fetches or search operations
        const isLargeFetch = params.limit === -1 || (params.limit && params.limit > 500);
        const isSearch = !!params.searchQuery;
        const timeoutMs = isLargeFetch
            ? 180 * 1000  // 3 minutes for large fetches
            : isSearch
                ? 60 * 1000   // 1 minute for search
                : 30 * 1000;  // 30 seconds default

        return postMessageWithResponse(WA_TOOL_COMMANDS.waQueryMessagesTool, params, { timeoutOverride: timeoutMs });
    }

    /**
     * Load older messages from phone for a given chat
     * Used by: waLoadMessagesFromPhoneTool, waQueryMessagesTool (when loadFromPhoneIfNeeded is true)
     *
     * First pages in history already synced to the local store via
     * loadEarlierMsgs; if that is exhausted, it fires an on-demand history
     * sync from the phone (the same `sendPeerDataOperationRequest(
     * HISTORY_SYNC_ON_DEMAND, {chatId})` call behind WAWeb's "Click here to
     * get older messages from your phone" prompt) and waits for the phone to
     * stream the history back. Chats whose history the phone cannot serve
     * return 0 added with canLoadMore=false.
     *
     * @param chatId The chat ID to load messages for
     * @returns Object with totalMessages, totalMessagesAddedByLoadFromPhoneIfNeeded, canLoadMore
     */
    public static async waLoadMessagesFromPhoneTool(chatId: string): Promise<WireResponse<WaLoadMessagesFromPhoneResult> | undefined> {
        // Use longer timeout as loading from phone can take time
        const timeoutMs = 60 * 1000; // 1 minute

        return postMessageWithResponse(
            WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool,
            { chatId },
            { timeoutOverride: timeoutMs },
        );
    }

    /**
     * Get a specific message by its key
     * Used by: waGetMessageByKeyTool
     */
    public static async waGetMessageByKeyTool(waMessageKey: string): Promise<WireResponse<WaGetMessageByKeyResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetMessageByKeyTool, { waMessageKey });
    }



    // `waGetMessageMediaTool` no longer lives on the ClientAction surface —
    // the thunk in `WhatsappThunks.ts` owns the page→content-script IPC
    // (via `EXTENSION_INTERNAL_COMMANDS.btMediaPrimitive`) + upload + PDF
    // extraction + BtSlim wire projection. Callers (`ai-assistant-button`,
    // `TranscribeMessageThunks`, `team-inbox`) import the thunk directly.



    public static async waGetMessagesAckDataTool(
        waMessageKeys?: Array<{ waMessageKey?: string; messageId?: string }>,
    ): Promise<WireResponse<WaGetMessagesAckDataResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetMessagesAckDataTool, { messageKeys: waMessageKeys ?? [] });
    }
    /**
     * Get unlogged WhatsApp message key from store
     */
    public static async getUnloggedWaMessageKeyIfExistInStore(
        chatId: string,
        userMessageId: string,
        dueDate: Date
        // Use the zod-derived contract type rather than re-declaring the shape here. The
        // hand-written copy had drifted: it omitted `searchMethod` and `globalStoreSearched`,
        // so callers could not read the fields the page context has been sending all along.
    ): Promise<WireResponse<GetUnloggedWaMessageKeyIfExistInStoreResult> | undefined> {
        const data = {
            chatId,
            userMessageId,
            dueDate,
        }
        return postMessageWithResponse(
            WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE,
            data,
            { timeoutOverride: 120 * 1000 },
        );
    }




    /**
     * React to an existing WhatsApp message (emoji).
     * Used by: waReactToMessageThunkTool
     */
    public static async waReactToMessageThunkTool(params: {
        waMessageKey: string;
        text: string;
    }): Promise<WireResponse<WaReactToMessageResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waReactToMessageThunkTool, {
            waMessageKey: params.waMessageKey,
            text: params.text,
        });
    }

    /**
     * Forward an existing WhatsApp message (by key) to one or more chats.
     * Used by: waForwardMessageTool
     */
    public static async waForwardMessageTool(params: {
        waMessageKey: string;
        toChatIds: string[];
        chatId?: string;
    }): Promise<WireResponse<WaForwardMessageResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waForwardMessageTool, {
            waMessageKey: params.waMessageKey,
            toChatIds: params.toChatIds,
            chatId: params.chatId,
        });
    }

    /**
     * Star or unstar an existing WhatsApp message (by key).
     * Used by: waStarMessageTool
     */
    public static async waStarMessageTool(params: {
        waMessageKey: string;
        star: boolean;
        chatId?: string;
    }): Promise<WireResponse<WaStarMessageResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waStarMessageTool, {
            waMessageKey: params.waMessageKey,
            star: params.star,
            chatId: params.chatId,
        });
    }

    /**
     * Delete an existing WhatsApp message (by key) — for me, or for everyone.
     * Used by: waDeleteMessageTool
     */
    public static async waDeleteMessageTool(params: {
        waMessageKey: string;
        forEveryone?: boolean;
        chatId?: string;
    }): Promise<WireResponse<WaDeleteMessageResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waDeleteMessageTool, {
            waMessageKey: params.waMessageKey,
            forEveryone: params.forEveryone,
            chatId: params.chatId,
        });
    }

    /**
     * Pin an existing WhatsApp message.
     * Used by: waPinMessageTool
     */
    public static async waPinMessageTool(params: {
        waMessageKey: string;
        duration?: number;
    }): Promise<WireResponse<WaPinMessageResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waPinMessageTool, {
            waMessageKey: params.waMessageKey,
            duration: params.duration,
        });
    }

    /**
     * Unpin an existing WhatsApp message.
     * Used by: waUnpinMessageTool
     */
    public static async waUnpinMessageTool(params: {
        waMessageKey: string;
    }): Promise<WireResponse<WaUnpinMessageResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waUnpinMessageTool, {
            waMessageKey: params.waMessageKey,
        });
    }

    /**
     * Create WhatsApp status (text or media).
     * Used by: createStatusTool
     */
    private static async convertPublicMediaUrlToDataUrl(url: string, fallbackMimeType?: string): Promise<string> {
        const response = await fetch(url, { credentials: 'omit' });
        if (!response.ok) {
            throw new Error(`Failed to download media URL (${response.status})`);
        }

        const blob = await response.blob();
        const normalizedBlob = fallbackMimeType && blob.type !== fallbackMimeType
            ? new Blob([blob], { type: fallbackMimeType })
            : blob;

        return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Failed to convert downloaded media to data URL'));
            reader.readAsDataURL(normalizedBlob);
        });
    }

    public static async createStatusTool(params: {
        supportedUserId?: string;
        /** FE#360 — scheduled `userMessage._id` (duplicate-guard tag). */
        userMessageId?: string;
        text?: string;
        content?: string;
        fileDataUrl?: string;
        fileName?: string;
        fileMimeType?: string;
    }): Promise<{
        success: boolean;
        action?: 'text' | 'media';
        messageId?: string;
        error?: string;
    }> {
        const originalFileDataUrl = String(params?.fileDataUrl || '').trim();
        const hasMedia = !!originalFileDataUrl;
        const text = String(params?.text || params?.content || '').trim();

        if (!hasMedia && !text) {
            return { success: false, error: 'Either text or fileDataUrl is required' };
        }

        try {
            let resolvedFileDataUrl = originalFileDataUrl;
            if (/^https?:\/\//i.test(originalFileDataUrl)) {
                try {
                    resolvedFileDataUrl = await WhatsappToolsClientActions.convertPublicMediaUrlToDataUrl(
                        originalFileDataUrl,
                        params?.fileMimeType
                    );
                } catch (urlFetchError) {
                    console.warn('[WhatsappToolsClientActions] createStatus URL->dataURL conversion failed, using original URL:', urlFetchError);
                    resolvedFileDataUrl = originalFileDataUrl;
                }
            }

            const timeoutOverrideMs = hasMedia ? 180 * 1000 : 60 * 1000;
            const response: any = await postMessageWithResponse({
                cmd: WA_TOOL_COMMANDS.createStatusTool,
                data: {
                    supportedUserId: params?.supportedUserId,
                    userMessageId: params?.userMessageId,
                    text: text || undefined,
                    content: text || undefined,
                    fileDataUrl: resolvedFileDataUrl || undefined,
                    fileName: params?.fileName,
                    fileMimeType: params?.fileMimeType,
                },
                timeoutOverride: timeoutOverrideMs,
            });

            if (!response) {
                return { success: false, error: 'No response received for createStatusTool' };
            }

            if (response?.success === false) {
                return { success: false, error: response?.error || 'Failed to create status' };
            }

            const responseData = response?.data ?? response;
            return {
                success: true,
                action: responseData?.action || (hasMedia ? 'media' : 'text'),
                messageId: responseData?.messageId ? String(responseData.messageId) : undefined,
            };
        } catch (error: any) {
            console.error('[WhatsappToolsClientActions] createStatus failed:', error);
            return {
                success: false,
                error: error?.message || 'Failed to create status',
            };
        }
    }

    // ==========================================
    // Message Ack Tools
    // ==========================================

    /**
     * Get message acknowledgment data for a single message by key
     * Used by: waGetMessageAckTool
     */
    public static async waGetMessageAckTool(args: { waMessageKey: string; chatId?: string }): Promise<WireResponse<WaGetMessageAckResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetMessageAckTool, args);
    }

    /**
     * Get a poll's current votes (who voted for which option)
     * Used by: waGetPollResultsTool
     */
    public static async waGetPollResultsTool(args: { waMessageKey: string; chatId?: string }): Promise<WireResponse<WaGetPollResultsResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetPollResultsTool, args);
    }

    // ==========================================
    // Session Domain Tools
    // ==========================================

    /**
     * Get WhatsApp "me" (current user) info
     * Used by: getWaMeInfoTool
     */
    public static async getWaMeInfoTool(): Promise<WireResponse<GetWaMeInfoResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.getWaMeInfoTool, {});
    }

    /**
     * Check if WhatsApp is connected
     * Used by: isWhatsAppConnectedThunkTool
     */
    public static async isWhatsAppConnectedThunkTool(): Promise<IsWhatsAppConnectedResult> {
        try {
            const response = await postMessageWithResponse(WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool, {});
            if (response && !isWireError(response)) {
                return {
                    connected: !!response.connected,
                    state: response.state,
                    stream: response.stream,
                    has_synced: response.has_synced,
                };
            }
        } catch (error) {
            console.error('[WhatsappToolsClientActions] isWhatsAppConnected failed:', error);
        }

        return {
            connected: false,
            state: undefined,
            stream: undefined,
            has_synced: undefined,
        };
    }

    /**
     * Trigger QR code timeout (force re-scan)
     * Used by: waTriggerQRTimeoutTool
     * Not in WaToolContractMap (extension-internal); legacy form.
     */
    public static async waTriggerQRTimeoutTool(): Promise<void> {
        await postMessageWithResponse(WA_TOOL_COMMANDS.waTriggerQRTimeoutTool, {});
    }

    /**
     * Trigger WhatsApp logout
     * Used by: waTriggerLogoutTool
     */
    public static async waTriggerLogoutTool(): Promise<WireResponse<WaTriggerLogoutResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waTriggerLogoutTool, {});
    }

    /**
     * Reload the WhatsApp page
     * Used by: waReloadPageTool
     * The page op (`reloadPage`) reads `input.reason` and forwards it to
     * `killMyPod(reason)` for diagnostics — pass it through on the wire.
     */
    public static async waReloadPageTool(reason: string = ""): Promise<WireResponse<WaReloadPageResult> | undefined> {
        assertWaPageBridge(WA_TOOL_COMMANDS.waReloadPageTool);
        return postMessageWithResponse(WA_TOOL_COMMANDS.waReloadPageTool, { reason });
    }

    // ==========================================
    // Validation Tools
    // ==========================================

    public static async waValidatePhoneNumberOrChatIdTool(
        chatId: string,
        options?: { useForceUsync?: boolean },
    ): Promise<WireResponse<WaValidatePhoneNumberOrChatIdResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool, { chatId, options });
    }



    // ==========================================
    // Link Preview Tools
    // ==========================================

    /**
     * Get link preview data for a URL
     * Used by: waGetLinkPreviewTool
     */
    public static async waGetLinkPreviewTool(link: string): Promise<WireResponse<WaGetLinkPreviewResult> | undefined> {
        return postMessageWithResponse(WA_TOOL_COMMANDS.waGetLinkPreviewTool, { link });
    }

    // ==========================================
    // Unified Contact & Chat Query (Extension Mode)
    // ==========================================

    /**
     * Unified Contact & Chat Query
     *
     * Sends a single request to the page context which handles all the logic:
     * - Fetching contacts and/or chats from WhatsApp stores
     * - Search/filtering if searchToken is provided
     * - Pagination with limit/offset
     *
     * All logic is executed in the page context for direct access to WhatsApp stores.
     * Only non-default parameters should be passed to minimize payload size.
     *
     * @param params Query parameters (only non-defaults should be included)
     * @param params.includeContacts Include contacts in response (default: true, only send if false)
     * @param params.includeChats Include chats in response (default: true, only send if false)
     * @param params.limit Maximum number of results (-1 for all)
     * @param params.offset Offset for pagination (only send if non-zero)
     * @param params.searchToken Search query for filtering
     * @param params.filterGroups Exclude groups from results (default: false, only send if true)
     * @param params.filterContacts Exclude individual contacts from results (default: false, only send if true)
     * @param params.filterNewsletters Exclude newsletters from results (default: false, only send if true)
     * @param params.includeLastMessage Include last message for chats (default: false, only send if true)
     */
    public static async waQueryContactsAndChatsTool(params: {
        includeContacts?: boolean;
        includeChats?: boolean;
        limit?: number;
        offset?: number;
        searchToken?: string;
        filterGroups?: boolean;
        filterContacts?: boolean;
        filterNewsletters?: boolean;
        includeWithoutName?: boolean;
        includeLastMessage?: boolean;
    }): Promise<WireResponse<WaQueryContactsAndChatsResult> | undefined> {
        // Use longer timeout for large data fetches (limit=-1 means all)
        const timeoutMs = (params.limit === -1 || (params.limit && params.limit > 500))
            ? 180 * 1000  // 180 seconds for large fetches
            : 30 * 1000;  // 30 seconds default

        return postMessageWithResponse(
            WA_TOOL_COMMANDS.waQueryContactsAndChatsTool,
            params,
            { timeoutOverride: timeoutMs },
        );
    }

    /**
     * Most-recent non-archived chats (pinned first) each with their latest
     * in-memory messages. One page-context round-trip.
     */
    public static async waGetLatestChatsAndMessagesTool(params: {
        numberOfChats?: number;
        numberOfMessages?: number;
    }): Promise<WireResponse<WaGetLatestChatsAndMessagesResult> | undefined> {
        return postMessageWithResponse(
            WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool,
            params,
        );
    }

    /**
     * All-chats page of the client-side IndexedDB message-vault, newest first,
     * cursor-paged. One page-context round-trip.
     */
    public static async waQueryVaultMessagesTool(
        params: WaQueryVaultMessagesArgs,
    ): Promise<WireResponse<WaQueryVaultMessagesResult> | undefined> {
        return postMessageWithResponse(
            WA_TOOL_COMMANDS.waQueryVaultMessagesTool,
            params,
        );
    }

    // ==========================================
    // Utility Methods
    // ==========================================


    public static async sendMessageViaWhatsapp(
        userMessage: UserMessage,
    ): Promise<WireResponse<WaSendMessageThunkResult> | undefined> {
        // IPC ceiling for the send RPC. The page handler runs wwebjs.processMediaData
        // (upload to WhatsApp CDN, encrypt, thumbnail prep) for media + poll sends; fleet
        // send-stage traces show media→group sends routinely take 60-144s (heavier than the
        // prior 10-30s estimate) on slow devices / large groups. The old 60s ceiling was
        // giving up on the IPC channel WHILE the send was still finishing — the message
        // LANDED but we saw "Timeout after 60000ms waiting for cmd" (ipc-timeout) and marked
        // it failed. Raised to 190s so the ceiling stays ABOVE the 180s content-side guard —
        // the channel outlives the guard, keeping late settlement observable. Text sends
        // complete well inside this; the ceiling only matters for heavy media paths.
        return postMessageWithResponse(
            WA_TOOL_COMMANDS.sendMessageThunkTool,
            { userMessage },
            { timeoutOverride: 190_000 },
        );
    }
}
